<script lang="ts" module>
  import type { ProductVariant, CommerceBlogPost, Category } from '@agapi/mlc-core';

  export interface NavbarSearchProps {}
</script>

<script lang="ts">
  import { page } from '$app/state';
  import { goto } from '$app/navigation';

  import { Debounced, watch } from 'runed'
  import { getContext, onMount } from 'svelte';
  import { writable } from 'svelte/store';
  import { Eye, XCircle } from 'phosphor-svelte';

  import { css } from '@agapi/ui/panda/css';
  import { icon, stack } from '@agapi/ui/panda/patterns';
  import { Button } from '@agapi/ui/button';
  import { type NavbarStore, NavbarSearch, NavbarSearchOverlay, NavbarLinkWrapper } from '@agapi/ui/navbar';

  import NavbarKeywordItem from './navbar-keyword-item.svelte';
  import { navbarSearchStyles } from './navbar-search.styles';
  import { formStyles, searchResultStyles } from './navbar.styles';

  import { ProductCard } from '../..';
  import { getCommerce } from '../../../services';
  import { getCookie } from '../../../utils/cookies.client';
  import { getLocaleFromUrl, rewriteUrl } from '../../../utils/locale.shared';

  import * as m from '$lib/parachute/messages';

  const navbar = getContext<NavbarStore>('navbar');

  const classes = navbarSearchStyles.raw();

  let value = $state<string>('');
  let items = $state<ProductVariant[]>([]);
  let count = $state(0);
  let autoComplete = $state<string[]>([]);
  let suggestions = $state<string[]>([]);
  let recommended = $state<ProductVariant[]>([]);
  let loading = $state(true);

  // New state variables for blog, category, and brand results
  let blogResults = $state<CommerceBlogPost[]>([]);
  let categoryResults = $state<Category[]>([]);
  let brandResults = $state<Category[]>([]);

  const debounced = new Debounced(() => value, 155);

  // A protected search function to prevent multiple calls to the search function
  const protectedSearch = async function(this: { lock: boolean }, text: string) {
    if (!this.lock) {
      this.lock = true;

      if (debounced.current) {
        const userId = getCookie<string>('__agapi_identifier') ?? 'default';
        const COMMERCE = getCommerce(window.fetch);
        const result = await COMMERCE.search({
          query: debounced.current,
          directSearch: true,
          includeAutoComplete: true,
          includeBlogResults: true,
          includeCategoryResults: true,
          includeBrandResults: true,
          blogResultsLimit: 3,
          categoryResultsLimit: 3,
          brandResultsLimit: 3,
          limit: 12
        }, getLocaleFromUrl(page.url), { userId });

        items = result.items;
        count = result.count;
        autoComplete = result.autoComplete?.length ? result.autoComplete : [];

        // Set the new state variables
        blogResults = result.blogResults || [];
        categoryResults = result.categoryResults || [];
        brandResults = result.brandResults || [];

        // Will unlock after a promise has been resolved and the states has been updated
        this.lock = loading = false;
      }
    }
  }.bind({ lock: false });

  // Load initial data
  onMount(async () => {
    const userId = getCookie<string>('__agapi_identifier') ?? 'default';

    // Parallel loading
    const COMMERCE = getCommerce(window.fetch);
    const [ newSuggestions, newRecommended ] = await Promise.allSettled([
      COMMERCE.getPopularSearches({ limit: 10 }, getLocaleFromUrl(page.url), { userId }),
      COMMERCE.getRecommendation({ limit: 10 }, getLocaleFromUrl(page.url), { userId })
    ]);

    suggestions = (newSuggestions?.status === 'fulfilled' ? newSuggestions.value.items : undefined) ?? [];
    recommended = (newRecommended?.status === 'fulfilled' ? newRecommended.value.items : undefined) ?? [];
  });

  watch(() => value, () => {
    loading = value.length > 0;
  });

  watch(() => debounced.current, () => {
    if (debounced.current) {
      protectedSearch(debounced.current);
    }
  });

  const searchUrl = $derived(rewriteUrl(page, `/search?q=${value}`));
</script>

<!-- Search -->
<NavbarSearch
  label={m.searchBarLabel()}
  formStyles={{ ...formStyles, zIndex: '99!' }}
  placeholder={m.searchBarPlaceholder()}
  onsubmit={() => {
    goto(searchUrl);
    value = '';
    navbar.closeSearch();
  }}
  maxLength={100}
  inputStyles={css.raw({ outlineWidth: '3px!' })}
  bind:value={value}
>
  <!-- Search Overlay -->
  <NavbarSearchOverlay
    css={{ bottom: 0, top: 'var(--navbarBottom)', md: { bottom: 'unset', top: 'var(--navbarHeight)' } }}
  >
    <div class={css(classes.stickyContainer)}>
      <Button onclick={() => navbar.closeSearch()} variant="text" css={classes.closeButton}>
        <XCircle class={icon({ size: 'lg' })}>
          <title>{m.iconClose()}</title>
        </XCircle>
      </Button>
    </div>

    <div class={css(searchResultStyles)}>
      {#if autoComplete.length}
      <div class={css(classes.suggestionsContainer)}>
        <div class={stack({ gap: '2' })}>
          <h5>{value.length > 0 ? m.suggestions('suggestions@@Förslag') : m.popularSearches()}</h5>

          <div class={stack({ gap: '0' })}>
            {#key value}
            {#each (value.length > 0 ? autoComplete : suggestions) as item (item)}
              <NavbarKeywordItem
                text={item}
                partialText={value.length > 0 ? value : item}
                href={`/search?q=${item}`}
                loading={loading}
              />
              {/each}
              {/key}
          </div>
        </div>
      </div>
      {/if}
      {#if brandResults.length}
      <div class={css(classes.brandResultsContainer)}>
        <div class={stack({ gap: '2' })}>
          <h5>{m.brands()}</h5>

          <div class={stack({ gap: '0' })}>
            {#key value}
            {#each (value.length > 0 ? brandResults : []) as brand (brand.id)}
              <NavbarKeywordItem
                text={brand.name}
                partialText={value}
                href={brand.path}
                loading={loading}
              />
              {/each}
              {/key}
          </div>
        </div>
      </div>
      {/if}
      {#if categoryResults.length}
      <div class={css(classes.categoryResultsContainer)}>
        <div class={stack({ gap: '2' })}>
          <h5>{m.categories()}</h5>

          <div class={stack({ gap: '0' })}>
            {#key value}
            {#each (value.length > 0 ? categoryResults : []) as category (category.id)}
              <NavbarKeywordItem
                text={category.name}
                partialText={value}
                href={category.path}
                loading={loading}
              />
              {/each}
              {/key}
          </div>
        </div>
      </div>
      {/if}
      {#if blogResults.length}
      <div class={css(classes.blogResultsContainer)}>
        <div class={stack({ gap: '2' })}>
          <h5>{m.articles()}</h5>

          <div class={stack({ gap: '0' })}>
            {#each (value.length > 0 ? blogResults : []) as blog (blog.id)}
              <NavbarKeywordItem
                text={blog.title}
                partialText={value}
                href={`/blog/${blog.slug}`}
                loading={loading}
              />
            {/each}
          </div>
        </div>
      </div>
      {/if}
      <div class={css(classes.spacer)}></div>
      <div class={css(classes.productsContainer)}>
        <div class={stack()}>
          <h5>{value.length > 0 ? m.products() : m.recommendedForYou()}</h5>

          <div data-scope="search" class={css({
            display: 'grid',
            gridTemplateColumns: {
              base: '1fr',
              sm: 'repeat(auto-fit, minmax(400px, 1fr))'
            },
            gridAutoFlow: 'row',
            gridGap: {
              base: '2',
              md: '6'
            }
          })}>
            {#if loading}
              {#each Array(6) as _}
                <ProductCard.Small loading={true} buttonLabel={m.buy()} css={{maxWidth: '700px!'}} />
              {/each}
            {:else}
              {#each (value.length > 0 ? items : recommended) as variant (variant.sku)}
                <NavbarLinkWrapper>
                <ProductCard.Small
                  product={variant}
                  buttonLabel={m.buy()}
                  ignoreDrawer
                  css={{maxWidth: '534px!'}}
                  />
                </NavbarLinkWrapper>
              {/each}
            {/if}
          </div>

          {#if value.length > 0 && items.length === 0 && !loading}
            <span>{m.noSearchResults('noSearchResults@@Inga sökresultat hittades')}</span>
          {/if}
        </div>

        {#if items.length > 0 && value.length > 0 && !loading}
          <div class={css(classes.fixedButton)}>
            <Button
              css={{
                flex: 1,
                borderBottomRadius: 0,
                sm: {
                  mt: '6',
                  flex: 'unset',
                  justifySelf: 'center',
                  borderBottomRadius: 'interactive'
                },
              }}
              onclick={() => {
                goto(searchUrl);
                navbar.closeSearch();
              }}
            >
              <Eye class={icon({ size: 'md' })}>
                <title>{m.iconView()}</title>
              </Eye>
              {m.searchResults({ value: count })}
            </Button>
          </div>
        {/if}
      </div>
    </div>
  </NavbarSearchOverlay>
</NavbarSearch>
